// Where a record was opened from (release 3.3, Back model B4 of the 7 Oct
// plan).
//
// A vendor, an assessment or an exit opened from Home, My work, Assessments,
// the register or another list keeps that list, with its filters, as the first
// breadcrumb for that visit: "My work > Cobalt HR Services", and the crumb
// returns to My work as the person left it. The origin is kept in the history
// entry's own state, so Back and Forward keep each visit's origin, and a
// record opened from a link with no origin falls back to its normal place
// ("Vendors > Cobalt HR Services").

// The lists a record can be opened from, by address, in the menu's words.
const LISTS: [RegExp, string][] = [
  [/^#\/?(home)?(\?.*)?$/, 'Home'],
  [/^#\/my-work(\?.*)?$/, 'My work'],
  [/^#\/vendors(\?.*)?$/, 'Vendors'],
  [/^#\/portfolio(\?.*)?$/, 'Portfolio'],
  [/^#\/assessments(\?.*)?$/, 'Assessments'],
  [/^#\/campaigns(\?.*)?$/, 'Bulk assessments'],
  [/^#\/findings(\?.*)?$/, 'Findings'],
  [/^#\/exceptions(\?.*)?$/, 'Risk acceptances'],
  [/^#\/sustain(\?.*)?$/, 'Reassessment'],
  [/^#\/exits(\?.*)?$/, 'Offboarding'],
  [/^#\/documents(\?.*)?$/, 'Documents'],
  [/^#\/procurement(\?.*)?$/, 'Purchase requests'],
  [/^#\/incidents(\?.*)?$/, 'Incidents'],
  [/^#\/risk(\?.*)?$/, 'Risk'],
  [/^#\/reports(\?.*)?$/, 'Reports'],
  [/^#\/questionnaires(\?.*)?$/, 'Questionnaires'],
  [/^#\/risk-models(\?.*)?$/, 'Risk models'],
  [/^#\/imports(\?.*)?$/, 'Import history'],
]

// The addresses that are records (a breadcrumb names where they were opened)
// and the one-page tasks (their Back link goes there): release 3.3, Back model
// B1, B3 and B4. A record's own tabs keep the origin the visit began with; a
// task opened from a record goes back to that record when it is finished.
const RECORDS =
  /^#\/(vendor\/(?!new)[^/?]+|assessment\/[^/?]+|exits\/[^/?]+|campaign\/[^/?]+|incident\/[^/?]+|questionnaire\/[^/?]+|risk-model\/[^/?]+|report\/[^/?]+)/

export type Origin = { label: string; href: string }

export function originOfAddress(hash: string): Origin | null {
  for (const [re, label] of LISTS) if (re.test(hash)) return { label, href: hash || '#/home' }
  return null
}

export function isRecordAddress(hash: string): boolean {
  return RECORDS.test(hash)
}

// Called on every move between addresses (App.tsx): arriving at a record
// from a list writes the list into the new history entry, once. Moving
// between a record's own tabs replaces the entry with its state kept
// (VendorDetail.tsx), so the origin stays with the visit.
export function noteOrigin(from: string, to: string): void {
  if (!isRecordAddress(to) && !isTaskAddress(to)) return
  try {
    let state = (history.state ?? {}) as Record<string, unknown>
    // A one-page task remembers the address it was opened from, so finishing
    // it can step back there (lib/finish.ts) instead of stacking a second
    // copy of that page on top. A move between the task's own steps (Back
    // and Forward through Send questionnaire) is not where it was opened from.
    if (isTaskAddress(to) && from && state.prev !== from && from.split('?')[0] !== to.split('?')[0]) {
      state = { ...state, prev: from }
      history.replaceState(state, '')
    }
    if (state.from) return
    const origin = originOfAddress(from)
    if (origin) history.replaceState({ ...state, from: origin }, '')
  } catch {
    // A browser that refuses history state shows the normal hierarchy.
  }
}

// The one-page tasks: the send screen, the questionnaire import, and Send
// questionnaire in steps from the Assessments list (lib/sendSteps.ts).
const TASKS = /^#\/(vendor\/[^/?]+\/invite|questionnaire\/import|assessments\/send)(\?|$)/

export function isTaskAddress(hash: string): boolean {
  return TASKS.test(hash)
}

// The address the task on screen was opened from, when it was opened from
// inside the console.
export function taskOpenedFrom(): string {
  try {
    const prev = ((history.state ?? {}) as { prev?: unknown }).prev
    return typeof prev === 'string' ? prev : ''
  } catch {
    return ''
  }
}

// The origin of the record on screen, or null for the normal hierarchy.
export function recordOrigin(): Origin | null {
  try {
    const from = ((history.state ?? {}) as { from?: Origin }).from
    return from && typeof from.label === 'string' && typeof from.href === 'string' ? from : null
  } catch {
    return null
  }
}
